'use client';

import { useEffect, useRef, type RefObject } from 'react';
import { trackSectionViewed } from '@/lib/posthog';

/**
 * Fire a `section_viewed` event the first time the referenced element scrolls
 * into view. Fires at most once per section per page load so funnels don't get
 * double-counted on re-scroll.
 *
 * No-op when `IntersectionObserver` is unavailable (e.g. SSR).
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLElement>(null);
 * useSectionViewTracking(ref, 'hero', 'landing');
 * return <section ref={ref}>…</section>;
 * ```
 */
export function useSectionViewTracking<T extends Element>(
  ref: RefObject<T | null>,
  section: string,
  page: string,
  // Fire once the element is at least 35% visible — enough to be confident the
  // user actually saw the section, not just a 1px sliver at the top.
  threshold: number = 0.35
): void {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasFiredRef.current) {
            hasFiredRef.current = true;
            trackSectionViewed(section, page);
            observer.disconnect();
            return;
          }
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref, section, page, threshold]);
}
